import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createNurse, deleteNurse, getNurses, getUnits, setNurseUnit } from '../../api/dashboard';
import { useAuth } from '../../auth/AuthContext';
import FilterSelect from '../../components/FilterSelect';
import PageHeader from '../../components/PageHeader';
import StatusMessage from '../../components/StatusMessage';
import { formatDateTime } from '../../utils/format';
import { adminErrorMessage } from './adminErrors';
import { ADMIN_NURSE_CREATION_ENABLED, CHARGE_NURSE_CREATION_ENABLED } from './features';
import { filterNurses, LAST_LOGIN_FILTERS, sortByLastLogin, unitOptions } from './nurseFilters';
import './Admin.css';

// Adds a nurse: the backend creates the Aren account and emails a temporary
// password. Admins choose the unit (required, so the nurse can sign in to the
// dashboard right away), and the nurse is then put on it. Charge nurses add
// to their own unit (`unitName`), which the backend sets itself.
function CreateNurseRow({ unitName, units = [], onCancel, onCreated }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [unitId, setUnitId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const commit = async () => {
    const fields = { firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim() };
    if (!fields.firstName && !fields.lastName && !fields.email) { onCancel(); return; }
    if (!fields.firstName) { setError('First name is required.'); return; }
    if (!fields.lastName) { setError('Last name is required.'); return; }
    if (!fields.email) { setError('Email is required.'); return; }
    if (!unitName && !unitId) { setError('Choose a unit.'); return; }
    setSaving(true);
    setError('');
    let created;
    try {
      created = await createNurse(fields);
    } catch (err) {
      setError(adminErrorMessage(err, 'Could not create the nurse account. Please try again.'));
      setSaving(false);
      return;
    }
    // The account exists and the email has gone out. If putting the nurse on
    // their unit fails, keep the new nurse and say how to finish.
    const unit = units.find((u) => u.id === unitId);
    if (!unit) { onCreated(created); return; }
    try {
      onCreated(await setNurseUnit(created.id, unit.id));
    } catch {
      onCreated(created, `but couldn't put them on ${unit.name}. Use the Unit dropdown on their row.`);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') commit();
    if (e.key === 'Escape') onCancel();
  };

  return (
    <div className="data-row data-row-creating">
      <div className="list-card-main">
        <div className="create-fields">
          <input ref={inputRef} className="create-input" value={firstName} onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name" aria-label="First name" disabled={saving} onKeyDown={onKeyDown} />
          <input className="create-input" value={lastName} onChange={(e) => setLastName(e.target.value)}
            placeholder="Last name" aria-label="Last name" disabled={saving} onKeyDown={onKeyDown} />
          <input type="email" className="create-input" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Email" aria-label="Email" disabled={saving} onKeyDown={onKeyDown} />
          {!unitName && (
            <select
              className={`unit-select create-unit-select${unitId ? ' unit-select-assigned' : ''}`}
              aria-label="New nurse's unit"
              value={unitId}
              onChange={(e) => setUnitId(e.target.value)}
              disabled={saving}
            >
              <option value="" disabled>Select unit</option>
              {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          )}
        </div>
        {error && <span className="field-error" role="alert">{error}</span>}
        <p className="admin-hint">
          {unitName
            ? `The nurse gets an Aren app account and a temporary password by email, and is added to ${unitName}.`
            : 'The nurse gets an Aren app account and a temporary password by email, and is put on the unit you choose.'}
        </p>
      </div>
      <div className="col-actions">
        <button type="button" className="title-button" onClick={commit} disabled={saving}>
          {saving ? 'Sending…' : unitName ? 'Send' : 'Create'}
        </button>
        <button type="button" className="text-button" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </div>
  );
}

function NurseRow({ nurse, canManage, units, onUpdated, onDeleted }) {
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState('');

  const assignUnit = async (unitId) => {
    if ((unitId || null) === (nurse.unit?.id || null)) return;
    setAssigning(true);
    setError('');
    try {
      onUpdated(await setNurseUnit(nurse.id, unitId));
    } catch (err) {
      setError(adminErrorMessage(err, "Could not change this nurse's unit. Please try again."));
    } finally {
      setAssigning(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError('');
    try {
      await deleteNurse(nurse.id);
      onDeleted(nurse.id);
    } catch (err) {
      setError(adminErrorMessage(err, 'Could not delete this nurse. Please try again.'));
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const open = () => navigate(`/nurses/${encodeURIComponent(nurse.id)}`);
  const stop = (e) => e.stopPropagation();

  return (
    <div
      className="data-row data-row-clickable"
      role="link"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) open(); }}
    >
      <div className="col-name">
        <span className="avatar" aria-hidden="true">{(nurse.name || nurse.email || '?').charAt(0).toUpperCase()}</span>
        <span className="person-name">{nurse.name || nurse.email}</span>
      </div>
      <span className="col-email">{nurse.email}</span>
      <span className="col-login">
        {(nurse.hasLoggedIn && formatDateTime(nurse.lastLoginAt)) || <span className="muted">Never logged in</span>}
      </span>
      {canManage && (
        <span className="col-unit" onClick={stop} onKeyDown={stop} role="presentation">
          <select
            className={`unit-select${nurse.unit ? ' unit-select-assigned' : ''}`}
            aria-label={`Unit for ${nurse.name || nurse.email}`}
            value={nurse.unit?.id || ''}
            onChange={(e) => assignUnit(e.target.value)}
            disabled={assigning}
          >
            <option value="">No unit</option>
            {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            {nurse.unit && !units.some((u) => u.id === nurse.unit.id) && (
              <option value={nurse.unit.id}>{nurse.unit.name}</option>
            )}
          </select>
        </span>
      )}
      {/* Only the admin's buttons (delete and its confirmation) keep clicks from
          reaching the row; a charge nurse's "View →" opens the nurse like the
          rest of the row. */}
      <div className="col-actions" onClick={canManage ? stop : undefined} onKeyDown={canManage ? stop : undefined} role="presentation">
        {!canManage ? (
          <span className="view-link">View →</span>
        ) : confirmDelete ? (
          <div className="delete-confirm">
            <button type="button" className="delete-confirm-yes" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
            <button type="button" className="text-button" onClick={() => setConfirmDelete(false)} disabled={deleting}>Cancel</button>
          </div>
        ) : (
          <button
            type="button"
            className="remove-button"
            onClick={() => setConfirmDelete(true)}
            title="Delete nurse account"
            aria-label={`Delete ${nurse.name || nurse.email}`}
          >
            ×
          </button>
        )}
      </div>
      {confirmDelete && !error && (
        <span className="field-error row-note">
          This permanently deletes {nurse.name || 'this nurse'}'s Aren app login. Their report sheets are kept.
        </span>
      )}
      {error && <span className="field-error row-note" role="alert">{error}</span>}
    </div>
  );
}

// Admins manage every nurse in the organization. Charge nurses see the nurses
// on their own unit (the backend scopes it) and will be able to add nurses to
// it once SSO is in place; until then the add button is disabled.
const Nurses = () => {
  const { nurse: user } = useAuth();
  const canManage = user?.role === 'admin';
  const canAdd = canManage ? ADMIN_NURSE_CREATION_ENABLED : CHARGE_NURSE_CREATION_ENABLED;
  const [nurses, setNurses] = useState(null);
  const [units, setUnits] = useState([]);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  // The nurse just added: { name, email, unit, problem }, shown until closed.
  const [added, setAdded] = useState(null);
  const [query, setQuery] = useState('');
  const [unitId, setUnitId] = useState('');
  const [login, setLogin] = useState('');
  const [loginSort, setLoginSort] = useState(null);
  const [copyStatus, setCopyStatus] = useState('');

  const load = useCallback(async () => {
    setError(null);
    try {
      setNurses((await getNurses()).nurses || []);
    } catch (err) {
      setError(adminErrorMessage(err, "We couldn't load the nurses."));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Admins assign nurses to units from this page.
  useEffect(() => {
    if (!canManage) return;
    getUnits()
      .then((res) => setUnits([...(res.units || [])].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))))
      .catch(() => setUnits([]));
  }, [canManage]);

  const all = nurses || [];
  const visible = sortByLastLogin(filterNurses(all, { query, unitId, login }), loginSort);
  const isFiltering = Boolean(query.trim() || unitId || login);
  const clearFilters = () => { setQuery(''); setUnitId(''); setLogin(''); };
  const cycleLoginSort = () => setLoginSort((s) => (s === null ? 'desc' : s === 'desc' ? 'asc' : null));

  // Copies the emails of the nurses shown, ready to paste into an email's To
  // or BCC field (e.g. filter to Last login "Never" to send a reminder).
  const visibleEmails = [...new Set(visible.map((n) => (n.email || '').trim()).filter(Boolean))];
  const emailCountLabel = `${visibleEmails.length} email${visibleEmails.length === 1 ? '' : 's'}`;
  const handleCopyEmails = async () => {
    try {
      await navigator.clipboard.writeText(visibleEmails.join(', '));
      setCopyStatus('copied');
    } catch {
      setCopyStatus('failed');
    }
    setTimeout(() => setCopyStatus(''), 2500);
  };

  return (
    <>
      <PageHeader
        title="Nurses"
        actions={(
          <div className="title-actions">
            {nurses && (
              <span className="count-pill">
                {nurses.length} {canManage ? 'account' : 'nurse'}{nurses.length === 1 ? '' : 's'}
              </span>
            )}
            <button
              type="button"
              className="reload-button"
              onClick={() => setCreating(true)}
              disabled={!canAdd || !nurses || creating}
              title={canAdd ? undefined : "Adding nurses isn't available yet."}
            >
              + Add Nurse
            </button>
          </div>
        )}
      />
      <div className="component-container">
        <div className="admin-content">
          {error && <StatusMessage kind="error" onRetry={load}>{error}</StatusMessage>}
          {!error && !nurses && <StatusMessage kind="loading">Loading nurses…</StatusMessage>}
          {nurses && (
            <>
              <div className="search-row">
                <input
                  type="search"
                  className="search-input"
                  placeholder={canManage ? 'Search by name, email or unit…' : 'Search by name or email…'}
                  aria-label="Search nurses"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                {canManage && <FilterSelect label="Unit" value={unitId} onChange={setUnitId} options={unitOptions(all)} />}
                <FilterSelect label="Last login" value={login} onChange={setLogin} options={LAST_LOGIN_FILTERS} />
                {isFiltering && (
                  <>
                    <span className="search-count">{visible.length} of {all.length}</span>
                    <button type="button" className="link-button" onClick={clearFilters}>Clear filters</button>
                  </>
                )}
                <button
                  type="button"
                  className={`copy-button${copyStatus === 'copied' ? ' copy-button-done' : ''}`}
                  onClick={handleCopyEmails}
                  disabled={visibleEmails.length === 0}
                  title="Copy the emails of the nurses shown below, comma-separated"
                >
                  {copyStatus === 'copied' ? `✓ Copied ${emailCountLabel}`
                    : copyStatus === 'failed' ? 'Copy failed. Check clipboard permissions.'
                      : `Copy ${emailCountLabel}`}
                </button>
              </div>

              {added && (
                <div className={added.problem ? 'nurse-added nurse-added-warning' : 'nurse-added'} role="status">
                  <span className="nurse-added-text">
                    <strong>{added.name}</strong> was added{added.unit && !added.problem ? <> to <strong>{added.unit}</strong></> : ''}{' '}
                    and emailed a temporary password at {added.email}{added.problem ? `, ${added.problem}` : '.'}
                  </span>
                  <button type="button" className="nurse-added-close" onClick={() => setAdded(null)} aria-label="Dismiss">×</button>
                </div>
              )}

              <div className="data-table" aria-label="Nurses">
                <div className="data-table-header">
                  <span className="col-name">Name</span>
                  <span className="col-email">Email</span>
                  <span
                    className="col-login"
                    aria-sort={loginSort === 'desc' ? 'descending' : loginSort === 'asc' ? 'ascending' : 'none'}
                  >
                    <button
                      type="button"
                      className={`sort-button${loginSort ? ' sort-button-active' : ''}`}
                      onClick={cycleLoginSort}
                      title={loginSort === 'desc' ? 'Sorted newest first' : loginSort === 'asc' ? 'Sorted oldest first' : 'Sort by last login'}
                    >
                      Last Login
                      <span className="sort-icon" aria-hidden="true">
                        {loginSort === 'desc' ? '↓' : loginSort === 'asc' ? '↑' : '↕'}
                      </span>
                    </button>
                  </span>
                  {canManage && <span className="col-unit">Unit</span>}
                  <span className="col-actions" />
                </div>
                {canAdd && creating && (
                  <CreateNurseRow
                    unitName={canManage ? null : user?.unit?.name || 'your unit'}
                    units={units}
                    onCancel={() => setCreating(false)}
                    onCreated={(nurse, problem) => {
                      setNurses((prev) => [...prev, nurse]);
                      setCreating(false);
                      setAdded({ name: nurse.name || nurse.email, email: nurse.email, unit: nurse.unit?.name, problem });
                    }}
                  />
                )}
                {visible.length === 0 && !creating && (
                  <div className="data-row">
                    <span className="muted">{isFiltering ? 'No nurses match your filters.' : 'No nurses yet.'}</span>
                  </div>
                )}
                {visible.map((nurse) => (
                  <NurseRow
                    key={nurse.id}
                    nurse={nurse}
                    canManage={canManage}
                    units={units}
                    onUpdated={(updated) => setNurses((prev) => prev.map((n) => (n.id === updated.id ? updated : n)))}
                    onDeleted={(id) => setNurses((prev) => prev.filter((n) => n.id !== id))}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Nurses;
